/** TanStack Query hooks for AMI measured analytics. */

import { useQuery } from '@tanstack/react-query'
import {
  getAmiStatus,
  getLoadProfile,
  getMeasuredLoading,
  getMeasuredLoadingGeoJSON,
  getModelComparison,
  getTransformerMeasuredLoading,
  getVoltageViolations,
  getVoltageViolationsGeoJSON,
} from '../api/amiAnalytics.ts'

export function useMeasuredLoading(params?: {
  window_days?: number
  feeder_id?: number
  avg_demand_kw?: number
}) {
  return useQuery({
    queryKey: ['ami', 'measured-loading', params],
    queryFn: () => getMeasuredLoading(params),
    staleTime: 60_000,
  })
}

export function useTransformerMeasuredLoading(
  transformerId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['ami', 'transformer-measured-loading', transformerId],
    queryFn: () => getTransformerMeasuredLoading(transformerId!),
    staleTime: 60_000,
    enabled: enabled && transformerId !== null,
    retry: false,
  })
}

export function useLoadProfile(transformerId: number | null, days = 14) {
  return useQuery({
    queryKey: ['ami', 'load-profile', transformerId, days],
    queryFn: () => getLoadProfile(transformerId!, days),
    staleTime: 60_000,
    enabled: transformerId !== null,
  })
}

export function useVoltageViolations(params?: {
  window_days?: number
  feeder_id?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['ami', 'voltage-violations', params],
    queryFn: () => getVoltageViolations(params),
    staleTime: 60_000,
  })
}

export function useModelComparison(feederId: number | null, windowDays = 30) {
  return useQuery({
    queryKey: ['ami', 'model-comparison', feederId, windowDays],
    queryFn: () => getModelComparison(feederId!, windowDays),
    staleTime: 60_000,
    enabled: feederId !== null,
  })
}

export function useAmiStatus() {
  return useQuery({
    queryKey: ['ami', 'status'],
    queryFn: getAmiStatus,
    staleTime: 60_000,
  })
}

export function useMeasuredLoadingGeoJSON(
  params:
    | { window_days?: number; feeder_id?: number; avg_demand_kw?: number }
    | undefined
    | null,
) {
  return useQuery({
    queryKey: ['ami', 'measured-loading-geojson', params],
    queryFn: () => getMeasuredLoadingGeoJSON(params ?? undefined),
    staleTime: 60_000,
    enabled: params !== undefined && params !== null,
  })
}

export function useVoltageViolationsGeoJSON(
  params: { window_days?: number; feeder_id?: number; limit?: number } | undefined | null,
) {
  return useQuery({
    queryKey: ['ami', 'voltage-violations-geojson', params],
    queryFn: () => getVoltageViolationsGeoJSON(params ?? undefined),
    staleTime: 60_000,
    enabled: params !== undefined && params !== null,
  })
}
